import { SYNC_CONFIG } from '@/config/sync';
import type { SyncJobMetadata, SyncJobDiagnostics } from '@/stores/realtime-sync-diagnostics-store';
import {
  useRealtimeSyncDiagnosticsStore,
  getRealtimeSyncDiagnostics,
} from '@/stores/realtime-sync-diagnostics-store';

type JobRunner = () => Promise<void>;

interface ScheduleOptions {
  readonly metadata?: SyncJobMetadata;
  readonly shouldRetry?: (error: unknown) => boolean;
}

const now = () => Date.now();

/**
 * Lightweight scheduler that ensures only one job per key runs at a time.
 * Emits diagnostics snapshots via `useRealtimeSyncDiagnosticsStore`.
 */
class RealtimeSyncEngine {
  private readonly inFlight = new Map<string, Promise<void>>();
  private readonly jobs = new Map<string, SyncJobDiagnostics>();
  private readonly retryTimers = new Map<string, ReturnType<typeof setTimeout>>();

  public schedule(key: string, runner: JobRunner, options: ScheduleOptions = {}): void {
    if (this.inFlight.has(key)) {
      return;
    }

    const existingTimer = this.retryTimers.get(key);
    if (existingTimer) {
      clearTimeout(existingTimer);
      this.retryTimers.delete(key);
    }

    const promise = (async () => {
      const metadata = options.metadata ??
        this.jobs.get(key)?.metadata ?? { entity: key.split(':')[0] };
      const previous = this.jobs.get(key);
      const attempts = (previous?.attempts ?? 0) + 1;
      const startedAt = now();

      const pendingState: SyncJobDiagnostics = {
        key,
        status: 'pending',
        attempts,
        lastStartedAt: startedAt,
        lastFinishedAt: previous?.lastFinishedAt,
        lastSuccessAt: previous?.lastSuccessAt,
        lastErrorAt: previous?.lastErrorAt,
        lastError: undefined,
        nextRetryAt: undefined,
        metadata,
      };

      this.jobs.set(key, pendingState);
      this.publish(pendingState);

      try {
        await runner();
        const finishedAt = now();
        const successState: SyncJobDiagnostics = {
          ...pendingState,
          status: 'success',
          lastFinishedAt: finishedAt,
          lastSuccessAt: finishedAt,
          lastError: undefined,
          lastErrorAt: undefined,
          nextRetryAt: undefined,
        };
        this.jobs.set(key, successState);
        this.publish(successState);
      } catch (error) {
        const finishedAt = now();
        const errorMessage = error instanceof Error ? error.message : String(error);
        const errorState: SyncJobDiagnostics = {
          ...pendingState,
          status: 'error',
          lastFinishedAt: finishedAt,
          lastErrorAt: finishedAt,
          lastError: errorMessage,
          nextRetryAt: undefined,
        };
        this.jobs.set(key, errorState);
        this.publish(errorState);

        const shouldRetry = options.shouldRetry?.(error) ?? true;
        const hasAttemptsRemaining = attempts < SYNC_CONFIG.maxRetries;
        if (shouldRetry && hasAttemptsRemaining) {
          const exponentialDelay = SYNC_CONFIG.initialRetryDelay * 2 ** (attempts - 1);
          const delay = Math.min(SYNC_CONFIG.maxRetryDelay, exponentialDelay);
          const nextRetryAt = finishedAt + delay;
          const retryState: SyncJobDiagnostics = {
            ...errorState,
            nextRetryAt,
          };
          this.jobs.set(key, retryState);
          this.publish(retryState);

          const timer = setTimeout(() => {
            this.retryTimers.delete(key);
            this.schedule(key, runner, options);
          }, delay);
          this.retryTimers.set(key, timer);
        }
      } finally {
        this.inFlight.delete(key);
      }
    })();

    this.inFlight.set(key, promise);
  }

  public getDiagnostics(): Record<string, SyncJobDiagnostics> {
    return getRealtimeSyncDiagnostics();
  }

  public reset(): void {
    this.inFlight.clear();
    this.jobs.clear();
    for (const timer of this.retryTimers.values()) {
      clearTimeout(timer);
    }
    this.retryTimers.clear();
    useRealtimeSyncDiagnosticsStore.getState().reset();
  }

  private publish(state: SyncJobDiagnostics): void {
    useRealtimeSyncDiagnosticsStore.getState().upsertJob(state);
  }
}

export const realtimeSyncEngine = new RealtimeSyncEngine();
